import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { cn } from '../lib/utils';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Textarea } from '../components/ui/textarea';
import {
  ArrowLeft,
  Edit,
  Check,
  X,
  Receipt,
  Printer,
  Loader2,
  Ban,
  DollarSign,
  RefreshCw,
  History,
  FileText,
} from 'lucide-react';
import Layout from '../components/Layout';
import settlementService from '../services/settlementService';
import authService from '../services/authService';
import FileAttachmentManager from '../components/FileAttachmentManager';

const SettlementDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const user = authService.getCurrentUser();
  const isAdmin = user?.role === 'admin';
  const isAccounting = user?.role === 'accounting';
  const canOperate = isAdmin || isAccounting; // Both admin and accounting can operate

  const [settlement, setSettlement] = useState(location.state?.settlementData || null);
  const [loading, setLoading] = useState(!location.state?.settlementData);
  const [error, setError] = useState('');
  const [actionDialog, setActionDialog] = useState({ open: false, action: '', reason: '' });
  const [refreshing, setRefreshing] = useState(false);

  const loadSettlement = async (force = false) => {
    if (!id) return;

    try {
      setLoading(true);
      setError('');
      const response = await settlementService.getSettlement(id, force ? Date.now() : null);
      setSettlement(response);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load settlement details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettlement();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadSettlement(true);
    setRefreshing(false);
  };

  const handleAction = (action) => {
    if (action === 'edit') {
      navigate(`/settlements/${id}/edit`);
    } else if (action === 'export') {
      // TODO: Implement export
      alert('导出功能开发中...');
    } else if (['submit', 'approve', 'reject', 'cancel', 'markPaid', 'requestRefund', 'processRefund'].includes(action)) {
      setActionDialog({ open: true, action, reason: '' });
    }
  };

  const handleConfirmAction = async () => {
    const { action, reason } = actionDialog;

    try {
      if (action === 'submit') {
        await settlementService.submitForApproval(id);
        setSettlement(prev => ({ ...prev, approval_status: 'pending', status: 'pending_approval' }));
        setError('');
        alert('结算单已提交审批！');
      } else if (action === 'approve') {
        await settlementService.approveSettlement(id);
        setSettlement(prev => ({ ...prev, approval_status: 'approved' }));
        setError('');
        alert('结算单已批准！');
      } else if (action === 'reject') {
        await settlementService.rejectSettlement(id, reason);
        setSettlement(prev => ({ ...prev, approval_status: 'rejected' }));
        setError('');
        alert('结算单已拒绝！');
      } else if (action === 'cancel') {
        await settlementService.deleteSettlement(id);
        setError('');
        alert('结算单已取消！');
        navigate('/settlements');
      } else if (action === 'markPaid') {
        await settlementService.markAsPaid(id);
        setSettlement(prev => ({ ...prev, payment_status: 'paid', status: 'paid' }));
        setError('');
        alert('结算单已标记为已付款！');
      } else if (action === 'requestRefund') {
        await settlementService.requestRefund(id, reason);
        setSettlement(prev => ({ ...prev, refund_status: 'requested' }));
        setError('');
        alert('退款申请已提交！');
      } else if (action === 'processRefund') {
        await settlementService.processRefund(id);
        setSettlement(prev => ({ ...prev, refund_status: 'processed', status: 'refunded' }));
        setError('');
        alert('退款已处理！');
      }

      setActionDialog({ open: false, action: '', reason: '' });
      loadSettlement(true);
    } catch (err) {
      setError(err.response?.data?.error || `操作失败`);
    }
  };

  const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'draft': return 'bg-muted text-muted-foreground';
      case 'pending_approval': return 'bg-info/10 text-info border-info/30';
      case 'approved': return 'bg-success/10 text-success border-success/30';
      case 'paid': return 'bg-primary/10 text-primary border-primary/30';
      case 'refunded': return 'bg-warning/10 text-warning border-warning/30';
      case 'cancelled': return 'bg-destructive/10 text-destructive border-destructive/30';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getApprovalStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending': return 'bg-warning/20 text-warning border-warning/30';
      case 'approved': return 'bg-success/20 text-success border-success/30';
      case 'rejected': return 'bg-destructive/20 text-destructive border-destructive/30';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getPaymentStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'unpaid': return 'bg-muted text-muted-foreground';
      case 'partially_paid': return 'bg-info/10 text-info border-info/30';
      case 'paid': return 'bg-success/10 text-success border-success/30';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return 'N/A';
    return new Intl.NumberFormat('zh-CN', {
      style: 'currency',
      currency: 'CNY'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusLabel = (status, type = 'status') => {
    if (type === 'approval') {
      switch (status?.toLowerCase()) {
        case 'pending': return '待审批';
        case 'approved': return '已批准';
        case 'rejected': return '已拒绝';
        default: return status || '待审批';
      }
    } else if (type === 'payment') {
      switch (status?.toLowerCase()) {
        case 'unpaid': return '未付款';
        case 'partially_paid': return '部分付款';
        case 'paid': return '已付款';
        default: return status || '未付款';
      }
    } else {
      switch (status?.toLowerCase()) {
        case 'draft': return '草稿';
        case 'pending_approval': return '待审批';
        case 'approved': return '已批准';
        case 'paid': return '已付款';
        case 'refund_requested': return '退款申请中';
        case 'refunded': return '已退款';
        case 'cancelled': return '已取消';
        default: return status || '草稿';
      }
    }
  };

  const canShowAction = (action) => {
    const status = settlement?.status?.toLowerCase();
    const approvalStatus = settlement?.approval_status?.toLowerCase();

    switch (action) {
      case 'edit':
        return status === 'draft' || (status === 'pending_approval' && approvalStatus === 'rejected');
      case 'submit':
        return status === 'draft';
      case 'approve':
        return canOperate && approvalStatus === 'pending';
      case 'reject':
        return canOperate && approvalStatus === 'pending';
      case 'cancel':
        return (status === 'draft' || approvalStatus === 'rejected') && canOperate;
      case 'markPaid':
        return approvalStatus === 'approved' && settlement?.payment_status !== 'paid' && canOperate;
      case 'requestRefund':
        return status === 'paid' && canOperate;
      case 'processRefund':
        return settlement?.refund_status === 'requested' && isAdmin; // Only admin can process refund
      default:
        return false;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto py-6 px-4">
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </div>
      </Layout>
    );
  }

  if (error && !settlement) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto py-6 px-4">
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="结算单详情" activeTab="settlements">
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription className="flex items-center justify-between">
              <span>{error}</span>
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setError('')}>
                <X className="h-4 w-4" />
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/settlements')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-foreground">
                {settlement?.settlement_id}
              </h1>
              <p className="text-sm text-muted-foreground">
                供应商：{settlement?.supplier_name}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {canShowAction('submit') && (
              <Button
                variant="default"
                onClick={() => handleAction('submit')}
              >
                <Receipt className="h-4 w-4 mr-2" />
                提交审批
              </Button>
            )}
            {canShowAction('approve') && (
              <Button
                variant="default"
                className="bg-success hover:bg-success/90"
                onClick={() => handleAction('approve')}
              >
                <Check className="h-4 w-4 mr-2" />
                批准
              </Button>
            )}
            {canShowAction('reject') && (
              <Button
                variant="outline"
                className="border-destructive text-destructive hover:bg-destructive/10"
                onClick={() => handleAction('reject')}
              >
                <X className="h-4 w-4 mr-2" />
                拒绝
              </Button>
            )}
            {canShowAction('cancel') && (
              <Button
                variant="outline"
                className="border-destructive text-destructive hover:bg-destructive/10"
                onClick={() => handleAction('cancel')}
              >
                <Ban className="h-4 w-4 mr-2" />
                取消
              </Button>
            )}
            {canShowAction('markPaid') && (
              <Button
                variant="default"
                className="bg-primary hover:bg-primary/90"
                onClick={() => handleAction('markPaid')}
              >
                <DollarSign className="h-4 w-4 mr-2" />
                标记付款
              </Button>
            )}
            {canShowAction('requestRefund') && (
              <Button
                variant="outline"
                onClick={() => handleAction('requestRefund')}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                申请退款
              </Button>
            )}
            {canShowAction('processRefund') && (
              <Button
                variant="default"
                className="bg-warning hover:bg-warning/90 text-warning-foreground"
                onClick={() => handleAction('processRefund')}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                处理退款
              </Button>
            )}
            {canShowAction('edit') && (
              <Button variant="outline" onClick={() => handleAction('edit')}>
                <Edit className="h-4 w-4 mr-2" />
                编辑
              </Button>
            )}
            <Button variant="outline" onClick={handleRefresh} disabled={refreshing}>
              <RefreshCw className={cn("h-4 w-4 mr-2", refreshing && "animate-spin")} />
              刷新
            </Button>
            <Button variant="outline" onClick={() => window.print()}>
              <Printer className="h-4 w-4 mr-2" />
              打印
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Settlement Information */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="border bg-card">
              <CardContent className="pt-6">
                <h2 className="text-lg font-semibold mb-2">结算单信息</h2>
                <Separator className="mb-4" />
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">结算单编号</p>
                    <p className="font-medium">{settlement?.settlement_id}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">供应商</p>
                    <p className="font-medium">{settlement?.supplier_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">状态</p>
                    <Badge className={cn('border', getStatusClass(settlement?.status))}>
                      {getStatusLabel(settlement?.status)}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">审批状态</p>
                    <Badge className={cn('border', getApprovalStatusClass(settlement?.approval_status))}>
                      {getStatusLabel(settlement?.approval_status, 'approval')}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">付款状态</p>
                    <Badge className={cn('border', getPaymentStatusClass(settlement?.payment_status))}>
                      {getStatusLabel(settlement?.payment_status, 'payment')}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">客户</p>
                    <p className="font-medium">{settlement?.client_name || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">创建日期</p>
                    <p>{formatDate(settlement?.created_at)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">提交日期</p>
                    <p>{formatDate(settlement?.submitted_at) || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">批准日期</p>
                    <p>{formatDate(settlement?.approved_at) || '-'}</p>
                  </div>
                  {settlement?.payment_date && (
                    <div>
                      <p className="text-sm text-muted-foreground">付款日期</p>
                      <p>{formatDate(settlement.payment_date)}</p>
                    </div>
                  )}
                  {(settlement?.notes || settlement?.comment) && (
                    <div className="col-span-2">
                      <p className="text-sm text-muted-foreground">备注</p>
                      <p>{settlement.notes || settlement.comment}</p>
                    </div>
                  )}
                  {settlement?.rejection_reason && (
                    <div className="col-span-2">
                      <p className="text-sm text-muted-foreground text-destructive">拒绝原因</p>
                      <p className="text-destructive">{settlement.rejection_reason}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Purchase Orders List */}
            <Card className="border bg-card">
              <CardContent className="pt-6">
                <h2 className="text-lg font-semibold mb-2">关联采购订单</h2>
                <Separator className="mb-4" />
                {settlement?.purchase_orders && settlement.purchase_orders.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-muted/50">
                          <th className="px-4 py-3 text-left font-medium">采购单号</th>
                          <th className="px-4 py-3 text-left font-medium">状态</th>
                          <th className="px-4 py-3 text-right font-medium">订单金额</th>
                          <th className="px-4 py-3 text-right font-medium">订购数量</th>
                          <th className="px-4 py-3 text-right font-medium">实际收货数量</th>
                          <th className="px-4 py-3 text-right font-medium">结算数量</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {settlement.purchase_orders.map((po, index) => (
                          <tr
                            key={po._id || index}
                            className="hover:bg-muted/30 cursor-pointer"
                            onClick={() => navigate(`/purchases/${po._id}/view`)}
                          >
                            <td className="px-4 py-3 font-medium text-primary">
                              {po.purchase_order_id}
                            </td>
                            <td className="px-4 py-3">
                              <Badge variant="outline" className={cn(
                                po.status === 'confirmed' ? 'bg-success/10 text-success' :
                                po.status === 'submitted' ? 'bg-info/10 text-info' :
                                'bg-muted'
                              )}>
                                {po.status === 'confirmed' ? '已确认' :
                                 po.status === 'submitted' ? '已提交' : '草稿'}
                              </Badge>
                            </td>
                            <td className="px-4 py-3 text-right">
                              {formatCurrency(po.total_amount || po.total_cost || 0)}
                            </td>
                            <td className="px-4 py-3 text-right">
                              {po.ordered_qty || po.total_qty || 0}
                            </td>
                            <td className="px-4 py-3 text-right">
                              {po.received_qty || 0}
                            </td>
                            <td className="px-4 py-3 text-right">
                              {po.settlement_qty || po.received_qty || 0}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>暂无关联采购订单</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Subcontract Orders List */}
            {settlement?.subcontract_orders && settlement.subcontract_orders.length > 0 && (
              <Card className="border bg-card">
                <CardContent className="pt-6">
                  <h2 className="text-lg font-semibold mb-2">关联外协单</h2>
                  <Separator className="mb-4" />
                  <div className="space-y-6">
                    {settlement.subcontract_orders.map((sc, scIndex) => (
                      <div key={sc._id || scIndex}>
                        <div className="flex justify-between items-center mb-2">
                          <span className="font-medium text-primary">{sc.subcontract_order_id}</span>
                          <span className="text-sm text-muted-foreground">{sc.supplier_name}</span>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="bg-muted/50">
                                <th className="px-4 py-3 text-left font-medium">产品</th>
                                <th className="px-4 py-3 text-left font-medium">生产单号</th>
                                <th className="px-4 py-3 text-right font-medium">完成数量</th>
                                <th className="px-4 py-3 text-right font-medium">结算数量</th>
                                <th className="px-4 py-3 text-right font-medium">单价</th>
                                <th className="px-4 py-3 text-right font-medium">小计</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y">
                              {(sc.items || []).map((item, itemIndex) => (
                                <tr key={itemIndex} className="hover:bg-muted/30">
                                  <td className="px-4 py-3">
                                    <div className="font-medium">{item.finished_product_name}</div>
                                    <div className="text-xs text-muted-foreground">{item.finished_product_code}</div>
                                  </td>
                                  <td className="px-4 py-3 text-muted-foreground">{item.production_order_id || '-'}</td>
                                  <td className="px-4 py-3 text-right">{item.completed_quantity || 0}</td>
                                  <td className="px-4 py-3 text-right">{item.settlement_qty ?? item.completed_quantity ?? 0}</td>
                                  <td className="px-4 py-3 text-right">{formatCurrency(item.unit_price || 0)}</td>
                                  <td className="px-4 py-3 text-right font-medium">
                                    {formatCurrency(item.line_total ?? (item.settlement_qty ?? item.completed_quantity ?? 0) * (item.unit_price || 0))}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Price Details */}
            <Card className="border bg-card">
              <CardContent className="pt-6">
                <h2 className="text-lg font-semibold mb-2">价格明细</h2>
                <Separator className="mb-4" />
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground"> subtotal</span>
                    <span className="font-medium">{formatCurrency(settlement?.subtotal || 0)}</span>
                  </div>
                  {settlement?.tax_rate > 0 && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-muted-foreground">
                        税率 ({(settlement.tax_rate * 100).toFixed(1)}%)
                      </span>
                      <span>{formatCurrency(settlement?.tax_amount || 0)}</span>
                    </div>
                  )}
                  {settlement?.discount_amount > 0 && (
                    <div className="flex justify-between items-center text-sm text-success">
                      <span className="text-muted-foreground">折扣</span>
                      <span>-{formatCurrency(settlement.discount_amount)}</span>
                    </div>
                  )}
                  <Separator />
                  <div className="flex justify-between items-center text-lg font-semibold">
                    <span>总计</span>
                    <span className="text-primary">{formatCurrency(settlement?.total_amount || 0)}</span>
                  </div>
                  {settlement?.paid_amount > 0 && (
                    <>
                      <Separator />
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">已付金额</span>
                        <span className="font-medium text-success">{formatCurrency(settlement.paid_amount)}</span>
                      </div>
                      {settlement?.total_amount && settlement?.paid_amount && (
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-muted-foreground">未付金额</span>
                          <span className="font-medium text-warning">
                            {formatCurrency(settlement.total_amount - settlement.paid_amount)}
                          </span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* File Attachments */}
            <FileAttachmentManager
              entityType="settlements"
              entityId={settlement?._id}
              title="结算单附件"
              className="mb-6"
            />
          </div>

          {/* Right Sidebar */}
          <div className="space-y-6">
            {/* Summary Card */}
            <Card className="border bg-card">
              <CardContent className="pt-6">
                <h2 className="text-lg font-semibold mb-4">结算摘要</h2>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">采购订单数量</p>
                    <p className="text-2xl font-bold">
                      {settlement?.purchase_orders?.length || 0}
                    </p>
                  </div>
                  <Separator />
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">结算总金额</p>
                    <p className="text-2xl font-bold text-primary">
                      {formatCurrency(settlement?.total_amount || 0)}
                    </p>
                  </div>
                  {settlement?.paid_amount > 0 && (
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">已付金额</p>
                      <p className="text-2xl font-bold text-success">
                        {formatCurrency(settlement.paid_amount)}
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Approval History */}
            <Card className="border bg-card">
              <CardContent className="pt-6">
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <History className="h-5 w-5" />
                  审批历史
                </h2>
                {settlement?.approval_history && settlement.approval_history.length > 0 ? (
                  <div className="space-y-3">
                    {settlement.approval_history.map((event, index) => (
                      <div key={index} className="flex gap-3 pb-3 border-b last:border-0">
                        <div className={cn(
                          "w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0",
                          event.action === 'approved' ? 'bg-success/20' :
                          event.action === 'rejected' ? 'bg-destructive/20' :
                          event.action === 'submitted' ? 'bg-primary/20' :
                          'bg-muted'
                        )}>
                          {event.action === 'approved' ? <Check className="h-4 w-4 text-success" /> :
                           event.action === 'rejected' ? <X className="h-4 w-4 text-destructive" /> :
                           event.action === 'submitted' ? <Receipt className="h-4 w-4 text-primary" /> :
                           <History className="h-4 w-4 text-muted-foreground" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium">
                            {event.action === 'approved' ? '批准' :
                             event.action === 'rejected' ? '拒绝' :
                             event.action === 'submitted' ? '提交审批' :
                             event.action === 'created' ? '创建' : '更新'}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {event.user || '系统'} • {formatDate(event.timestamp)}
                          </p>
                          {event.reason && (
                            <p className="text-xs text-muted-foreground mt-1">
                              原因：{event.reason}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4 text-muted-foreground">
                    <History className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">暂无审批历史记录</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Action Dialog */}
        <Dialog
          open={actionDialog.open}
          onOpenChange={(open) => !open && setActionDialog({ open: false, action: '', reason: '' })}
        >
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                {actionDialog.action === 'submit' && '提交审批'}
                {actionDialog.action === 'approve' && '批准结算单'}
                {actionDialog.action === 'reject' && '拒绝结算单'}
                {actionDialog.action === 'cancel' && '取消结算单'}
                {actionDialog.action === 'markPaid' && '标记为已付款'}
                {actionDialog.action === 'requestRefund' && '申请退款'}
                {actionDialog.action === 'processRefund' && '处理退款'}
              </DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-sm text-muted-foreground">
                {actionDialog.action === 'submit' && '确定要提交此结算单进行审批吗？'}
                {actionDialog.action === 'approve' && `确定要批准结算单 ${settlement?.settlement_id} 吗？`}
                {actionDialog.action === 'reject' && `确定要拒绝结算单 ${settlement?.settlement_id} 吗？`}
                {actionDialog.action === 'cancel' && `确定要取消结算单 ${settlement?.settlement_id} 吗？此操作不可撤销。`}
                {actionDialog.action === 'markPaid' && `确定要将结算单 ${settlement?.settlement_id} 标记为已付款吗？`}
                {actionDialog.action === 'requestRefund' && '确定要申请退款吗？'}
                {actionDialog.action === 'processRefund' && '确定要处理退款吗？此操作不可撤销。'}
              </p>
              {(actionDialog.action === 'reject' || actionDialog.action === 'requestRefund') && (
                <div className="mt-4 space-y-2">
                  <Label htmlFor="reason">
                    {actionDialog.action === 'reject' ? '拒绝原因' : '退款原因'}
                  </Label>
                  <Textarea
                    id="reason"
                    rows={4}
                    value={actionDialog.reason}
                    onChange={(e) => setActionDialog(prev => ({ ...prev, reason: e.target.value }))}
                    placeholder={`请输入${actionDialog.action === 'reject' ? '拒绝' : '退款'}原因...`}
                  />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setActionDialog({ open: false, action: '', reason: '' })}
              >
                取消
              </Button>
              <Button
                onClick={handleConfirmAction}
                variant={
                  actionDialog.action === 'reject' || actionDialog.action === 'cancel' ? 'destructive' :
                  actionDialog.action === 'processRefund' ? 'warning' :
                  'default'
                }
                disabled={(actionDialog.action === 'reject' || actionDialog.action === 'requestRefund') && !actionDialog.reason.trim()}
              >
                {actionDialog.action === 'submit' && '提交'}
                {actionDialog.action === 'approve' && '批准'}
                {actionDialog.action === 'reject' && '拒绝'}
                {actionDialog.action === 'cancel' && '取消'}
                {actionDialog.action === 'markPaid' && '标记付款'}
                {actionDialog.action === 'requestRefund' && '申请退款'}
                {actionDialog.action === 'processRefund' && '处理退款'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default SettlementDetailPage;
